/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { InvoiceResponse } from "../../../models/invoice/InvoiceResponse";
import { strings } from "../../../common/Strings/Strings";
import { InvoiceStatus } from "../../../models/invoice/InvoiceStatus";
import { downloadInvoicePdf, printInvoicePdf } from "../../../util/FileUtils";
import { getGeneralError } from "../../../util/helperFunctions";
import logger from "../../../util/logger";
import AddPayment from "./AddPayment";
import WithdrawInvoice from "./WithdrawInvoice";
import InvoiceApi from "../../../api/InvoiceApi";
import Button from "../../../components/Button";
import { ArrowDownTray } from "../../../common/Icons/ArrowDownTray";
import { Printer } from "../../../common/Icons/Printer";
import { ArrowUnturnLeft } from "../../../common/Icons/ArrowUnturnLeft";
import Tooltip from "../../../components/Tooltip";
import { Cog } from "../../../common/Icons/Cog";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { getInvoiceTypeLink } from "../InvoiceListing/InvoiceListRow";

interface Props {
  invoice: InvoiceResponse;
  isDropdown?: boolean;
  isPairInvoice?: boolean;
  reload?(): void;
  setError(message: string | null): void;
}

const InvoiceActions = ({
  invoice,
  isDropdown = true,
  isPairInvoice = false,
  reload,
  setError,
}: Props): ReactElement => {
  const [isPrinting, setPrinting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState<boolean>(false);
  const [showCancelWarningModal, setShowCancelWarningModal] = useState<boolean>(false);
  const navigate = useNavigate();

  const deleteInvoice = async () => {
    if (!invoice) {
      return;
    }

    setIsDeleting(true);

    try {
      await InvoiceApi.deletePendingInvoice(invoice.id);
      navigate(getInvoiceTypeLink(invoice));
    } catch (err) {
      setError(await getGeneralError(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      {isDropdown ? (
        <Dropdown className="min-w-48">
          <Dropdown.Toggle />
          <Dropdown.Content>
            <li hidden={invoice?.costAccounting?.status !== "UNRESOLVED" || invoice?.valid === false}>
              <Button
                className="dropdown-item"
                onClick={() => {
                  setIsAddPaymentModalOpen(true);
                }}
                variant="link"
              >
                {strings.markAsPaid}
              </Button>
            </li>
            <li hidden={invoice.status === InvoiceStatus.IN_PROGRESS || invoice.valid === false}>
              <Button
                className="dropdown-item"
                onClick={async () => {
                  try {
                    await downloadInvoicePdf(invoice);
                  } catch (err) {
                    setError(await getGeneralError(err));
                  }
                }}
                variant="link"
              >
                {strings.downloadInvoicePDF}
              </Button>
            </li>
            <li hidden={invoice.status === InvoiceStatus.IN_PROGRESS || invoice.valid === false}>
              <Button
                className="dropdown-item"
                disabled={isPrinting}
                onClick={async () => {
                  setPrinting(true);
                  try {
                    if (invoice) {
                      await printInvoicePdf(invoice, () => setPrinting(false));
                    }
                  } catch (err) {
                    logger.error(err);
                    setError(await getGeneralError(err));
                    setPrinting(false);
                  }
                }}
                variant="link"
              >
                {strings.print}
              </Button>
            </li>
            <li
              hidden={
                invoice?.valid === false ||
                invoice?.costAccounting?.status === "WITHDRAWN" ||
                invoice.status === InvoiceStatus.IN_PROGRESS
              }
            >
              <Button
                className="dropdown-item text-left"
                onClick={() => {
                  setShowCancelWarningModal(true);
                }}
                variant="link"
              >
                {strings.withdrawInvoice}
              </Button>
            </li>
            <li>
              <Link
                className="dropdown-item text-left"
                to={`/invoices/edit/${invoice?.id}/copy`}
              >
                {strings.copyAsNew}
              </Link>
            </li>
            <li hidden={invoice.status !== InvoiceStatus.IN_PROGRESS}>
              <Button
                className="dropdown-item text-left"
                disabled={isDeleting}
                onClick={deleteInvoice}
                variant="link"
              >
                {strings.withdrawReadyForPayment}
              </Button>
            </li>
          </Dropdown.Content>
        </Dropdown>
      ) : (
        <div className="flex items-center space-x-3">
          <Button
            hidden={invoice?.costAccounting?.status !== "UNRESOLVED" || invoice?.valid === false}
            variant="primary"
            fullWidth={false}
            onClick={() => {
              setIsAddPaymentModalOpen(true);
            }}
          >
            {strings.markAsPaid}
          </Button>
          <div
            hidden={
              invoice?.valid === false ||
              invoice?.costAccounting?.status === "WITHDRAWN" ||
              invoice.status === InvoiceStatus.IN_PROGRESS
            }
          >
            <Tooltip content={strings.withdrawInvoice} placement="top">
              <Button
                className="tw-link"
                fullWidth={false}
                onClick={() => {
                  setShowCancelWarningModal(true);
                }}
                variant="icon"
              >
                <ArrowUnturnLeft className="w-5 h-5" />
              </Button>
            </Tooltip>
          </div>
          <div hidden={invoice.status === InvoiceStatus.IN_PROGRESS || invoice.valid === false}>
            <Tooltip content={strings.downloadInvoicePDF} placement="top">
              <Button
                className="tw-link"
                onClick={async () => {
                  try {
                    await downloadInvoicePdf(invoice);
                  } catch (err) {
                    setError(await getGeneralError(err));
                  }
                }}
                variant="icon"
              >
                <ArrowDownTray />
              </Button>
            </Tooltip>
          </div>
          <div hidden={invoice.status === InvoiceStatus.IN_PROGRESS || invoice.valid === false}>
            <Tooltip content={strings.print} placement="top">
              <Button
                className="tw-link"
                disabled={isPrinting}
                onClick={async () => {
                  setPrinting(true);
                  try {
                    if (invoice) {
                      await printInvoicePdf(invoice, () => setPrinting(false));
                    }
                  } catch (err) {
                    logger.error(err);
                    setError(await getGeneralError(err));
                    setPrinting(false);
                  }
                }}
                variant="icon"
              >
                <Printer />
              </Button>
            </Tooltip>
          </div>
          <li hidden={!isPairInvoice}>
            <Tooltip content={strings.invoiceDetails} placement="top">
              <Link to={`/invoices/clinic/${invoice?.id}`}>
                <Button className="tw-link" variant="icon">
                  <Cog />
                </Button>
              </Link>
            </Tooltip>
          </li>
        </div>
      )}
      <AddPayment
        close={() => {
          setIsAddPaymentModalOpen(false);
        }}
        invoice={invoice}
        isOpen={isAddPaymentModalOpen}
        reload={() => {
          setIsAddPaymentModalOpen(false);
          if(reload){
            reload();
          }
        }}
      />
      <WithdrawInvoice
        close={() => {
          setShowCancelWarningModal(false);
        }}
        invoice={invoice}
        isOpen={showCancelWarningModal}
        reload={() => {
          setShowCancelWarningModal(false);
          if(reload){
            reload();
          }
        }}
      />
    </div>
  );
};

export default InvoiceActions;
